import { zodResolver } from "@hookform/resolvers/zod";
import { AmountPresetButtons } from "Components/Customer/Payment/AmountPresetButtons";
import { EditableAmountCard } from "Components/Customer/Payment/EditableAmountCard";
import { PaymentLayout } from "Components/Customer/Payment/PaymentForms/PaymentLayout";
import {
  type CollectorFormModel,
  collectorPaymentSchema,
} from "Components/Customer/Payment/PaymentForms/schema";
import { TipSection } from "Components/Customer/Payment/PaymentForms/TipSection";
import { computeTipAmount } from "Components/Customer/Payment/PaymentForms/tipUtils";
import { usePayAccountWarmUp } from "Components/Customer/Payment/PaymentForms/usePayAccountWarmUp";
import { useScanAndPaySubmit } from "Components/Customer/Payment/PaymentForms/useScanAndPaySubmit";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import type {
  BankAccountListItem,
  PayPointScanViewDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { pickMainBankAccount } from "Utils/AccountUtils";
import { getAmountPresetsForCurrency } from "Utils/AmountPresetUtils";
import { useForm } from "react-hook-form";

type Props = {
  paypointPublicID: string;
  paypoint: PayPointScanViewDto;
  bankAccounts: Array<BankAccountListItem>;
};

export const VariableCollectorPaymentForm = ({
  paypoint: { tipsEnabled, predefinedAmountCurrencyCode: currencyCode },
  bankAccounts,
  paypointPublicID,
}: Props) => {
  const { t } = useResource();
  // The paypoint's denomination fixes the payment currency — default to that rail.
  const defaultBankAccountPublicID = pickMainBankAccount(
    bankAccounts,
    currencyCode ?? undefined,
  )?.publicID;

  const { handleSubmit, control, watch, setError, setValue } =
    useForm<CollectorFormModel>({
      resolver: zodResolver(collectorPaymentSchema),
      defaultValues: {
        tipMode: "none",
        bankAccountPublicID: defaultBankAccountPublicID,
      },
      mode: "onChange",
    });

  const bankAccountPublicID = watch("bankAccountPublicID");
  const tipMode = watch("tipMode") ?? "none";
  const customTipAmount = watch("customTipAmount");
  const customAmount = watch("customAmount");

  const { submit, isSubmitting } = useScanAndPaySubmit();
  const { onBankAccountChange } = usePayAccountWarmUp(
    defaultBankAccountPublicID,
  );

  // Every Collector paypoint carries a currency (BE-validated and backfilled);
  // a missing one is broken data — never guess a currency for a payment.
  if (!currencyCode) {
    return <ErrorText>{t(Resources.Errors.DataLoadingError)}</ErrorText>;
  }

  const billAmount = customAmount ?? 0;
  const tipAmount = computeTipAmount(tipMode, customTipAmount, billAmount);
  const totalAmount = billAmount + tipAmount;
  const amountPresets = getAmountPresetsForCurrency(currencyCode);

  const onSubmit = async () => {
    const selectedBankAccount = bankAccounts.find(
      (b) => b.publicID === bankAccountPublicID,
    );

    if (!selectedBankAccount) {
      return setError("bankAccountPublicID", {
        message: t(Resources.Common.Required),
      });
    }

    if (!customAmount || customAmount <= 0) {
      return setError("customAmount", {
        message: t(Resources.Common.Required),
      });
    }

    await submit({
      paypointPublicID,
      bankAccount: selectedBankAccount,
      amount: billAmount,
      tipAmount,
      onError: (message) => setError("customAmount", { message }),
    });
  };

  const isPaymentDisabled =
    !bankAccountPublicID || !customAmount || customAmount <= 0 || isSubmitting;

  return (
    <PaymentLayout
      payPointPublicID={paypointPublicID}
      bankAccounts={bankAccounts}
      control={control}
      totalAmount={totalAmount}
      currencyCode={currencyCode}
      isPaymentDisabled={isPaymentDisabled}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(onSubmit)}
      onBankAccountChange={onBankAccountChange}
    >
      <EditableAmountCard
        value={customAmount}
        currencyCode={currencyCode}
        onChange={(v) =>
          setValue("customAmount", v, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
      />
      <AmountPresetButtons
        presets={amountPresets}
        currencyCode={currencyCode}
        onSelect={(amount) =>
          setValue("customAmount", amount, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
      />
      {tipsEnabled && (
        <TipSection
          tipMode={tipMode}
          currencyCode={currencyCode}
          customTipAmount={customTipAmount}
          onTipModeChange={(mode) =>
            setValue("tipMode", mode, { shouldDirty: true })
          }
          onCustomTipAmountChange={(amount) =>
            setValue("customTipAmount", amount, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
      )}
    </PaymentLayout>
  );
};
